import React, { useCallback, useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { Card, CardContent, Typography, Button, Box, Alert, CircularProgress } from '@mui/material';
import { CheckCircle, Cancel, PersonAdd } from '@mui/icons-material';
import api from '../services/api';
import { ACCEPT_INVITE } from '../config/Config';
import { useAuth } from '../contexts/AuthContext';

const normalizeEmail = (email) => (email || '').trim().toLowerCase();

const AcceptInvitePage = () => {
    const { token: pathToken } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const [inviteData, setInviteData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [actionLoading, setActionLoading] = useState(false);
    const [actionResult, setActionResult] = useState(null);
    const [accountMismatch, setAccountMismatch] = useState(null);
    const { user, logout } = useAuth();

    // ✅ FIXED: Get token from query parameters (as sent in email), URL path, or navigation state
    const [token, setToken] = useState(searchParams.get('token') || pathToken || location.state?.inviteData?.token);
    
    // Debug log for token
    console.log('AcceptInvitePage - Initial token:', token, 'pathToken:', pathToken, 'location.state:', location.state);

    useEffect(() => {
        // ✅ NEW LOGIC: Check if coming from email verification
        if (location.state?.fromVerification && location.state?.inviteData) {
            console.log('Setting invite data from verification:', location.state.inviteData);
            setInviteData(location.state.inviteData);
            // ✅ FIXED: Set the token from verification data
            if (location.state.inviteData.token) {
                console.log('Setting token from verification:', location.state.inviteData.token);
                setToken(location.state.inviteData.token);
            }
            // ✅ FIXED: Set loading to false since we have the data
            setLoading(false);
            return;
        }

        // ✅ NEW LOGIC: Check if coming from Google registration with invite
        if (location.state?.isInviteBased && location.state?.inviteToken) {
            console.log('Setting token from Google registration:', location.state.inviteToken);
            setToken(location.state.inviteToken);
            // Don't return early - let it continue to fetch invite data
            // setLoading(false);
            // return;
        }

        // ✅ NEW LOGIC: Check if coming from registration with pending student invites
        // This logic is no longer used - invitations are now marked as used during registration
        if (location.state?.hasPendingStudentInvites) {
            console.log('User has pending student invites after registration:', location.state);
            // Redirect to login since invitations are now handled during registration
            navigate('/login', { 
                replace: true,
                state: { 
                    message: 'Registration successful! You can now accept the invitation after login.',
                    email: location.state.email
                }
            });
            return;
        }

        // Otherwise fetch invite data normally
        if (token) {
            fetchInviteData();
        } else {
            // Check if this might be a Google registration without invitation
            if (location.state?.fromGoogleRegistration) {
                setError('You have successfully registered through Google! Please go to the login page to sign in.');
            } else {
                setError('No invitation token provided. This page is only for accepting teacher invitations. If you\'re trying to register, please go back to the registration page.');
            }
            setLoading(false);
        }
    }, [token, location.state]);

    const fetchInviteData = async () => {
        try {
            setLoading(true);
            setError(null);
            setAccountMismatch(null);
            const response = await api.get(`${ACCEPT_INVITE}?token=${token}`);
            
            // ✅ DEBUG: Log the response data to see what we're getting
            console.log('AcceptInvitePage - Backend response:', response.data);
            
            // ✅ NEW LOGIC: Handle all responses based on backend action field
            if (response.data && response.data.action) {
                const { action, inviteToken, existingUserInactive } = response.data;
                
                // Handle the action based on backend instruction
                switch (action) {
                    case 'show_account_mismatch':
                        setAccountMismatch({
                            message: response.data.detail,
                            invitedEmail: response.data.invitedEmail || response.data.email,
                            currentUserEmail: response.data.currentUserEmail || user?.email,
                        });
                        break;

                    case 'show_registration_prompt':
                        // User needs to register first (either new user or inactive existing user)
                        // ✅ FIXED: Use the comprehensive data from backend response
                        setInviteData({
                            needsRegistration: true,
                            inviteType: response.data.invite_type || 'teacher',
                            email: response.data.email,
                            role: response.data.role || 'STUDENT',
                            planCode: response.data.planCode || 'student_starter',
                            inviteToken: inviteToken || token,
                            teacherName: response.data.teacherName,
                            teacherEmail: response.data.teacherEmail,
                            tenantName: response.data.tenantName,
                            parentName: response.data.parentName,
                            parentEmail: response.data.parentEmail,
                            familyName: response.data.familyName,
                            // ✅ ADDED: Include all the comprehensive details from backend
                            planName: response.data.planName,
                            planPrice: response.data.planPrice,
                            planCurrency: response.data.planCurrency,
                            academicYear: response.data.academicYear,
                            existingUserInactive: existingUserInactive || false
                        });
                        break;
                        
                    case 'show_invitation_actions':
                        // User can accept/reject the invitation directly
                        // ✅ FIXED: Use the comprehensive data from backend response
                        setInviteData({
                            needsRegistration: false,
                            inviteType: response.data.invite_type || 'teacher',
                            email: response.data.email,
                            role: response.data.role || 'STUDENT',
                            planCode: response.data.plan_code || 'student_starter',
                            inviteToken: inviteToken || token,
                            teacherName: response.data.teacher_name,
                            tenantName: response.data.tenant_name,
                            parentName: response.data.parentName,
                            parentEmail: response.data.parentEmail,
                            familyName: response.data.familyName,
                            // ✅ ADDED: Include all the comprehensive details from backend
                            teacher_name: response.data.teacher_name,
                            teacher_email: response.data.teacher_email,
                            tenant_name: response.data.tenant_name,
                            plan_name: response.data.plan_name,
                            plan_price: response.data.plan_price,
                            plan_currency: response.data.plan_currency,
                            subject_name: response.data.subject_name,
                            academic_year: response.data.academic_year,
                            academicYear: response.data.academicYear,
                            created_at: response.data.created_at,
                            expires_at: response.data.expires_at,
                            createdAt: response.data.createdAt,
                            expiresAt: response.data.expiresAt,
                            relationship_id: response.data.relationship_id,
                            relationship_active: response.data.relationship_active,
                            relationship_accepted: response.data.relationship_accepted,
                            existingUserInactive: false
                        });
                        break;
                        
                    case 'show_error_message':
                        // Backend indicates an error - show error message
                        setError(response.data.detail || 'Invalid or expired invitation');
                        break;
                        
                    default:
                        // Fallback - treat as regular invitation data
                        setInviteData(response.data);
                }
            } else {
                // Fallback for responses without action field
                if (response.data.needsRegistration) {
                    setInviteData(response.data);
                } else {
                    setInviteData(response.data);
                }
            }
        } catch (error) {
            console.error('Error fetching invite data:', error);
            
            // ✅ NEW LOGIC: Handle error response based on backend action field
            if (error.response?.data && error.response.data.action) {
                const { action: errorAction, detail } = error.response.data;
                
                // Handle error action if needed
                switch (errorAction) {
                    case 'show_account_mismatch':
                        setAccountMismatch({
                            message: detail,
                            invitedEmail: error.response.data.invitedEmail,
                            currentUserEmail: error.response.data.currentUserEmail || user?.email,
                        });
                        break;

                    case 'show_error_message':
                        setError(detail || 'Invalid or expired invitation');
                        break;
                        
                    default:
                        setError('Failed to fetch invitation details');
                }
            } else {
                setError('Failed to fetch invitation details');
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const invitedEmail = inviteData?.email;
        const currentEmail = user?.email;

        if (invitedEmail && currentEmail && normalizeEmail(invitedEmail) !== normalizeEmail(currentEmail)) {
            setAccountMismatch({
                invitedEmail,
                currentUserEmail: currentEmail,
                message: `This invitation was sent to ${invitedEmail}, but you are currently signed in as ${currentEmail}. Please switch accounts to continue.`,
            });
        } else if (invitedEmail && currentEmail && normalizeEmail(invitedEmail) === normalizeEmail(currentEmail)) {
            setAccountMismatch(null);
        }
    }, [inviteData, user]);

    const handleAction = async (action) => {
        try {
            setActionLoading(true);
            console.log('handleAction - token:', token, 'action:', action);
            const response = await api.post(`${ACCEPT_INVITE}?token=${token}`, { action });
            
            // ✅ NEW LOGIC: Handle response based on backend action field
            if (response.data && response.data.action) {
                const { action: responseAction, message } = response.data;
                
                setActionResult({
                    success: true,
                    message: message
                });
                
                // Handle the action based on backend instruction
                switch (responseAction) {
                    case 'redirect_to_dashboard':
                        // Redirect to dashboard after a delay
                        setTimeout(() => {
                            navigate('/dashboard', {
                                replace: true,
                                state: {
                                    message: message,
                                    fromInvite: true
                                }
                            });
                        }, 2000);
                        break;
                    case 'redirect_to_home':
                        setTimeout(() => {
                            navigate('/', { replace: true });
                        }, 600);
                        break;
                        
                    case 'show_success_message':
                        // Just show success message, no redirect
                        setTimeout(() => {
                            navigate('/dashboard', {
                                replace: true,
                                state: {
                                    message: message,
                                    fromInvite: true
                                }
                            });
                        }, 2000);
                        break;
                        
                    default:
                        // Default behavior - redirect to dashboard
                        setTimeout(() => {
                            navigate('/dashboard', {
                                replace: true,
                                state: {
                                    message: message,
                                    fromInvite: true
                                }
                            });
                        }, 2000);
                }
            } else {
                // Fallback for responses without action field
                setActionResult({
                    success: true,
                    message: response.data.message
                });
                
                // Redirect after a delay
                setTimeout(() => {
                    navigate('/dashboard');
                }, 2000);
            }
            
        } catch (error) {
            console.error(`Error ${action}ing invite:`, error);
            
            // ✅ NEW LOGIC: Handle error response based on backend action field
            if (error.response?.data && error.response.data.action) {
                const { action: errorAction, detail } = error.response.data;
                
                setActionResult({
                    success: false,
                    message: detail || `Failed to ${action} invitation`
                });
                
                // Handle error action if needed
                switch (errorAction) {
                    case 'show_account_mismatch':
                        setAccountMismatch({
                            message: detail,
                            invitedEmail: error.response.data.invitedEmail,
                            currentUserEmail: error.response.data.currentUserEmail || user?.email,
                        });
                        break;

                    case 'show_error_message':
                        // Just show error message, no redirect
                        break;
                    default:
                        // Default error handling
                        break;
                }
            } else {
                setActionResult({
                    success: false,
                    message: error.response?.data?.detail || `Failed to ${action} invitation`
                });
            }
        } finally {
            setActionLoading(false);
        }
    };

    const handleSwitchAccount = async () => {
        const invitedEmail = accountMismatch?.invitedEmail || inviteData?.email;
        const from = token ? `/accept-invite?token=${encodeURIComponent(token)}` : '/accept-invite';

        try {
            await logout();
        } finally {
            navigate('/login', {
                replace: true,
                state: {
                    from,
                    email: invitedEmail,
                    message: invitedEmail
                        ? `Please sign in as ${invitedEmail} to accept this invitation.`
                        : 'Please sign in with the invited account to accept this invitation.',
                },
            });
        }
    };

    const handleRegister = useCallback(() => {
        console.log('handleRegister called');
        console.log('inviteData:', inviteData);
        console.log('token:', token);
        console.log('Navigating to /invite-register with state:', {
            inviteEmail: inviteData.email,
            inviteToken: inviteData.inviteToken || token
        });
        
        // Navigate to invitation registration page
        navigate('/invite-register', { 
            state: { 
                inviteEmail: inviteData.email,
                inviteToken: inviteData.inviteToken || token,
                teacherName: inviteData.teacherName,
            }
        });
    }, [inviteData, navigate, token]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (
            inviteData?.needsRegistration &&
            inviteData?.inviteType !== 'family' &&
            !inviteData?.existingUserInactive
        ) {
            handleRegister();
        }
    }, [handleRegister, inviteData]);

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
                <Alert severity="error" sx={{ maxWidth: 600 }}>
                    <Typography variant="body1" sx={{ whiteSpace: 'pre-line', mb: 2 }}>
                        {error}
                    </Typography>
                    {!token && (
                        <Box sx={{ mt: 2 }}>
                            {location.state?.fromGoogleRegistration ? (
                                <Button 
                                    variant="contained" 
                                    onClick={() => navigate('/login')}
                                    sx={{ mr: 1 }}
                                >
                                    Go to Login
                                </Button>
                            ) : (
                                <Button 
                                    variant="contained" 
                                    onClick={() => navigate('/register')}
                                >
                                    Go to Registration
                                </Button>
                            )}
                        </Box>
                    )}
                </Alert>
            </Box>
        );
    }

    if (accountMismatch) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
                <Card sx={{ maxWidth: 640, width: '100%' }}>
                    <CardContent sx={{ p: 4 }}>
                        <Alert severity="warning" sx={{ mb: 3 }}>
                            <Typography variant="h6" gutterBottom>
                                Use the invited account
                            </Typography>
                            <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
                                {accountMismatch.message}
                            </Typography>
                        </Alert>

                        <Box sx={{ textAlign: 'left', mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                            <Typography variant="body2" color="text.secondary">
                                <strong>Invited email:</strong> {accountMismatch.invitedEmail || inviteData?.email || 'Not available'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                <strong>Current session:</strong> {accountMismatch.currentUserEmail || user?.email || 'Not signed in'}
                            </Typography>
                        </Box>

                        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                            <Button variant="contained" onClick={handleSwitchAccount}>
                                Sign out and switch account
                            </Button>
                            <Button variant="outlined" onClick={() => navigate('/dashboard')}>
                                Stay with current account
                            </Button>
                        </Box>
                    </CardContent>
                </Card>
            </Box>
        );
    }

    // ✅ NEW LOGIC: Show registration prompt for users who need to register
    console.log('Checking needsRegistration:', inviteData?.needsRegistration);
    console.log('Full inviteData:', inviteData);
    
    if (inviteData?.needsRegistration) {
        const isFamilyInvite = inviteData.inviteType === 'family';
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
                <Card sx={{ maxWidth: 600, width: '100%' }}>
                    <CardContent sx={{ textAlign: 'center', p: 4 }}>
                        <PersonAdd sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
                        
                        <Typography variant="h5" gutterBottom>
                            {inviteData.existingUserInactive 
                                ? 'Complete Your Registration' 
                                : isFamilyInvite ? 'Join Your Family Account' : 'Join Your Teacher\'s Group'
                            }
                        </Typography>
                        
                        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                            {inviteData.existingUserInactive 
                                ? `You have an account but it's not active. Complete your registration to continue.`
                                : isFamilyInvite
                                    ? `You've been invited by ${inviteData.parentName} to join the ${inviteData.familyName}. Please register to continue.`
                                    : `You've been invited by ${inviteData.teacherName} to join their group. Please register to continue.`
                            }
                        </Typography>
                        
                        <Box sx={{ textAlign: 'left', mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                            <Typography variant="subtitle2" gutterBottom>
                                Invitation Details:
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                <strong>{isFamilyInvite ? 'Parent' : 'Teacher'}:</strong> {isFamilyInvite ? inviteData.parentName : inviteData.teacherName}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                <strong>{isFamilyInvite ? 'Parent Email' : 'Teacher Email'}:</strong> {isFamilyInvite ? inviteData.parentEmail : inviteData.teacherEmail}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                <strong>{isFamilyInvite ? 'Family' : 'Organization'}:</strong> {isFamilyInvite ? inviteData.familyName : inviteData.tenantName}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                <strong>Role:</strong> {inviteData.role}
                            </Typography>
                            {inviteData.academicYear && (
                                <Typography variant="body2" color="text.secondary">
                                    <strong>Grade:</strong> {inviteData.academicYear}
                                </Typography>
                            )}
                            {inviteData.planName && (
                                <Typography variant="body2" color="text.secondary">
                                    <strong>Plan:</strong> {inviteData.planName} ({inviteData.planPrice} {inviteData.planCurrency})
                                </Typography>
                            )}
                        </Box>
                        
                        <Button
                            variant="contained"
                            size="large"
                            onClick={() => {
                                console.log('Button clicked!');
                                handleRegister();
                            }}
                            sx={{ minWidth: 200 }}
                        >
                            {inviteData.existingUserInactive ? 'Complete Registration' : 'Register Now'}
                        </Button>
                    </CardContent>
                </Card>
            </Box>
        );
    }

    // Show action buttons for active users
    const isFamilyInvite = inviteData?.inviteType === 'family';
    return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
            <Card sx={{ maxWidth: 600, width: '100%' }}>
                <CardContent sx={{ textAlign: 'center', p: 4 }}>
                    {actionResult ? (
                        <Box>
                            {actionResult.success ? (
                                <CheckCircle sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
                            ) : (
                                <Cancel sx={{ fontSize: 64, color: 'error.main', mb: 2 }} />
                            )}
                            <Typography variant="h6" gutterBottom>
                                {actionResult.success ? 'Success!' : 'Error'}
                            </Typography>
                            <Typography color={actionResult.success ? 'success.main' : 'error.main'}>
                                {actionResult.message}
                            </Typography>
                        </Box>
                    ) : (
                        <>
                            <Typography variant="h5" gutterBottom>
                                {isFamilyInvite ? 'Accept Family Invitation' : `Accept invitation from teacher ${inviteData?.teacher_name || inviteData?.teacherName || ''}`.trim()}
                            </Typography>
                            
                            <Box sx={{ textAlign: 'left', mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                                <Typography variant="subtitle2" gutterBottom>
                                    Invitation Details:
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    <strong>{isFamilyInvite ? 'Parent' : 'Teacher'}:</strong> {isFamilyInvite ? (inviteData?.parentName) : (inviteData?.teacher_name || inviteData?.teacherName)}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    <strong>{isFamilyInvite ? 'Parent Email' : 'Teacher Email'}:</strong> {isFamilyInvite ? inviteData?.parentEmail : (inviteData?.teacher_email || inviteData?.teacherEmail)}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    <strong>{isFamilyInvite ? 'Family' : 'Organization'}:</strong> {isFamilyInvite ? inviteData?.familyName : (inviteData?.tenant_name || inviteData?.tenantName)}
                                </Typography>

                                {inviteData?.plan_name && (
                                    <Typography variant="body2" color="text.secondary">
                                        <strong>Plan:</strong> {inviteData.plan_name} ({inviteData.plan_price} {inviteData.plan_currency})
                                    </Typography>
                                )}
                                {inviteData?.role && (
                                    <Typography variant="body2" color="text.secondary">
                                        <strong>Role:</strong> {inviteData.role}
                                    </Typography>
                                )}
                                {(inviteData?.created_at || inviteData?.createdAt) && (
                                    <Typography variant="body2" color="text.secondary">
                                        <strong>Invited on:</strong> {new Date(inviteData.created_at || inviteData.createdAt).toLocaleDateString()}
                                    </Typography>
                                )}
                                {(inviteData?.expires_at || inviteData?.expiresAt) && (
                                    <Typography variant="body2" color="text.secondary">
                                        <strong>Expires:</strong> {new Date(inviteData.expires_at || inviteData.expiresAt).toLocaleDateString()}
                                    </Typography>
                                )}
                                {(inviteData?.academic_year || inviteData?.academicYear) && (
                                    <Typography variant="body2" color="text.secondary">
                                        <strong>Grade:</strong> {inviteData.academic_year || inviteData.academicYear}
                                    </Typography>
                                )}
                                {inviteData?.relationship_id && (
                                    <Typography variant="body2" color="text.secondary">
                                        <strong>Status:</strong> {inviteData.relationship_accepted ? 'Already accepted' : 'Pending acceptance'}
                                    </Typography>
                                )}
                            </Box>
                            
                            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                                {isFamilyInvite
                                    ? "You've been invited to join this family account. Would you like to accept or decline this invitation?"
                                    : "You've been invited to join this teacher's group. Would you like to accept or decline this invitation?"}
                            </Typography>
                            
                            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                                <Button
                                    variant="contained"
                                    color="success"
                                    startIcon={<CheckCircle />}
                                    onClick={() => handleAction('accept')}
                                    disabled={actionLoading}
                                    sx={{ minWidth: 120 }}
                                >
                                    Accept
                                </Button>
                                <Button
                                    variant="outlined"
                                    color="error"
                                    startIcon={<Cancel />}
                                    onClick={() => handleAction('reject')}
                                    disabled={actionLoading}
                                    sx={{ minWidth: 120 }}
                                >
                                    Reject
                                </Button>
                            </Box>
                            
                            {actionLoading && (
                                <Box sx={{ mt: 2 }}>
                                    <CircularProgress size={24} />
                                </Box>
                            )}
                        </>
                    )}
                </CardContent>
            </Card>
        </Box>
    );
};

export default AcceptInvitePage;
